// Named views: partial view states with an id and a title, from data/views/*.toml
// (format: schemas/views.schema.json). The prebuild step 15-views.mjs gathers them
// into src/generated/views.json. A view applies on top of the default state, never
// on top of whatever the app shows, so a view always looks the same.

import { FIELDS, setField, type Field } from "./fields";
import { DEFAULT_VIEW_STATE, cloneState, type PartialViewState, type ViewState } from "./view-state";
import generated from "../generated/views.json" with { type: "json" };

export interface NamedView {
  id: string;
  title: string;
  description: string;
  /** The data file it came from, relative to the repository root. */
  file: string;
  state: PartialViewState;
}

/** Every named view, in file name order and then in file order. */
export const NAMED_VIEWS: readonly NamedView[] = (generated as { views: NamedView[] }).views;

export function findView(id: string | null, views: readonly NamedView[] = NAMED_VIEWS): NamedView | undefined {
  return id === null ? undefined : views.find((v) => v.id === id);
}

/**
 * Lays a partial state over `base`. Values that are missing keep the base's; values
 * that are invalid or unknown keep the base's too and are reported in `issues`.
 */
export function overlay(base: ViewState, partial: unknown, where = "state"): { state: ViewState; issues: string[] } {
  const state = cloneState(base);
  const issues: string[] = [];
  walk(partial, [], where, (field, value, at) => {
    if (field.codec.check(value)) setField(state, field, value);
    else issues.push(`${at}: ${JSON.stringify(value)} is not a valid value`);
  }, issues);
  return { state, issues };
}

/** The state a named view stands for: the defaults with the view laid over them, and `view` set to its id. */
export function viewBase(view: NamedView | undefined): { state: ViewState; issues: string[] } {
  if (view === undefined) return { state: cloneState(DEFAULT_VIEW_STATE), issues: [] };
  const { state, issues } = overlay(DEFAULT_VIEW_STATE, view.state, `${view.file} view ${view.id}`);
  state.view = view.id;
  return { state, issues };
}

function walk(
  value: unknown,
  path: string[],
  at: string,
  visit: (field: Field, value: unknown, at: string) => void,
  issues: string[],
): void {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    issues.push(`${at}: must be a table`);
    return;
  }
  for (const [key, child] of Object.entries(value)) {
    const childPath = [...path, key];
    const childAt = `${at}.${key}`;
    const field = FIELDS.find((f) => samePath(f.path, childPath));
    if (field !== undefined) {
      visit(field, child, childAt);
    } else if (FIELDS.some((f) => startsWith(f.path, childPath))) {
      walk(child, childPath, childAt, visit, issues);
    } else {
      issues.push(`${childAt}: not a view-state field`);
    }
  }
}

function samePath(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((k, i) => k === b[i]);
}

function startsWith(path: readonly string[], prefix: readonly string[]): boolean {
  return path.length > prefix.length && prefix.every((k, i) => k === path[i]);
}
